import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { employeesAPI } from '../services/api';

const WeeklyView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});      // employee_id -> bool
  const [showNotes, setShowNotes] = useState({});    // employee_id -> bool
  const [search, setSearch] = useState('');
  const [hideIdle, setHideIdle] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await employeesAPI.getWeeklyView();
      setData(res.data);
    } catch (err) {
      console.error('Error loading weekly view:', err);
      setError('Could not load the weekly view. Check that the backend is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded(s => ({ ...s, [id]: !s[id] }));
  const toggleNotes = (id) => setShowNotes(s => ({ ...s, [id]: !s[id] }));

  const dayLabel = (dateStr) =>
    new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
  const dayNum = (dateStr) =>
    new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const rangeLabel = (a, b) => {
    if (!a || !b) return '';
    const f = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${f(a)} – ${f(b)}`;
  };

  const loadBadge = (pct) => {
    if (pct > 100) return 'badge-danger';
    if (pct >= 80) return 'badge-warning';
    if (pct > 0)   return 'badge-success';
    return 'badge-info';
  };

  if (loading) return <div className="loading">Loading this week's allocation…</div>;

  if (error) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h2 className="page-title">Weekly Team View</h2>
          </div>
        </div>
        <div className="card"><p>{error}</p></div>
      </div>
    );
  }

  if (!data) return null;

  const { week_days: weekDays, week_start, week_end, today, resources } = data;

  const filtered = resources.filter(r => {
    if (hideIdle && r.tasks.length === 0) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.employee_name.toLowerCase().includes(q) ||
      (r.role || '').toLowerCase().includes(q) ||
      r.tasks.some(t =>
        t.task_name.toLowerCase().includes(q) ||
        (t.project_name || '').toLowerCase().includes(q)
      )
    );
  });

  const totalTasks = resources.reduce((s, r) => s + r.tasks.length, 0);
  const overloaded = resources.filter(r =>
    weekDays.some(d => (r.day_totals[d] || 0) > 100)
  ).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Weekly Team View</h2>
          <p className="page-subtitle">
            {rangeLabel(week_start, week_end)} · {filtered.length} resources · {totalTasks} tasks
            {overloaded > 0 && ` · ${overloaded} over-allocated`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={load}>🔄 Refresh</button>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="grid grid-2">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Resource, task or project…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Display</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px' }}>
              <input
                type="checkbox"
                checked={hideIdle}
                onChange={e => setHideIdle(e.target.checked)}
              />
              <span>Hide resources with no tasks this week</span>
            </label>
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <div className="empty-state-text">No resources match this week</div>
          </div>
        </div>
      ) : filtered.map(r => {
        const isOpen = !!expanded[r.employee_id];
        const notesOpen = !!showNotes[r.employee_id];
        const peak = Math.max(...weekDays.map(d => r.day_totals[d] || 0), 0);

        return (
          <div className="card" key={r.employee_id}>
            {/* Resource header */}
            <div
              className="card-header"
              style={{ cursor: 'pointer', marginBottom: isOpen ? 20 : 0, paddingBottom: isOpen ? 15 : 0, borderBottom: isOpen ? undefined : 'none' }}
              onClick={() => toggle(r.employee_id)}
            >
              <div>
                <span className="card-title">
                  {isOpen ? '▾' : '▸'} {r.employee_name}
                </span>
                <p className="page-subtitle" style={{ marginTop: 4 }}>
                  {r.role || '—'}{r.department ? ` · ${r.department}` : ''} · {r.tasks.length} task{r.tasks.length === 1 ? '' : 's'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {r.notes.length > 0 && (
                  <span className="badge badge-primary">📝 {r.notes.length}</span>
                )}
                <span className={`badge ${loadBadge(peak)}`}>Peak {peak}%</span>
              </div>
            </div>

            {isOpen && (
              <>
                {r.tasks.length === 0 ? (
                  <p style={{ color: '#7f8c8d' }}>No tasks allocated this week.</p>
                ) : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Project</th>
                          {weekDays.map(d => (
                            <th key={d} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {dayLabel(d)}
                              <div style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                                {dayNum(d)}
                              </div>
                            </th>
                          ))}
                          <th style={{ textAlign: 'right' }}>Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.tasks.map(t => (
                          <tr key={t.task_id}>
                            <td>
                              <strong>{t.task_name}</strong>
                              <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {t.status_name && (
                                  <span className="badge badge-info">{t.status_name}</span>
                                )}
                                {t.due_this_week && (
                                  <span className="badge badge-warning">
                                    📅 Due {dayNum(t.due_date)}{t.is_revised ? ' (revised)' : ''}
                                  </span>
                                )}
                                {!t.due_this_week && t.is_revised && t.forecast_end && (
                                  <span className="badge badge-info">
                                    ↪ Revised to {dayNum(t.forecast_end)}
                                  </span>
                                )}
                                {t.is_slipping && (
                                  <span className="badge badge-danger">⚠ Slipping</span>
                                )}
                                {t.is_unplanned && (
                                  <span className="badge badge-warning">Unplanned</span>
                                )}
                                {t.tagged_note_ids.length > 0 && (
                                  <span className="badge badge-primary">
                                    📝 {t.tagged_note_ids.length}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              {t.project_id ? (
                                <Link to={`/projects/${t.project_id}`}>{t.project_name}</Link>
                              ) : '—'}
                            </td>
                            {weekDays.map(d => {
                              const v = t.daily_allocations[d] || 0;
                              return (
                                <td key={d} style={{ textAlign: 'center' }}>
                                  {v > 0
                                    ? <strong>{v}%</strong>
                                    : <span style={{ color: '#cbd5e1' }}>–</span>}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'right' }}>
                              <strong>{Math.round(t.week_total / 5)}%</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan={2}>Day total</th>
                          {weekDays.map(d => {
                            const tot = r.day_totals[d] || 0;
                            const colour = tot > 100 ? '#e74c3c' : tot >= 80 ? '#f39c12' : '#27ae60';
                            return (
                              <th key={d} style={{ textAlign: 'center', color: colour }}>
                                {tot}%{tot > 100 ? ' ⚠' : ''}
                                {d === today && (
                                  <div style={{ fontSize: 10, fontWeight: 400, color: '#3498db', textTransform: 'none' }}>
                                    today
                                  </div>
                                )}
                              </th>
                            );
                          })}
                          <th style={{ textAlign: 'right' }}>{r.week_avg}%</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* Project notes */}
                {r.notes.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => toggleNotes(r.employee_id)}
                    >
                      📝 {notesOpen ? 'Hide' : 'Show'} project notes ({r.notes.length})
                    </button>

                    {notesOpen && (
                      <div style={{ marginTop: 15 }}>
                        {Object.entries(
                          r.notes.reduce((acc, n) => {
                            const key = n.project_id || 'none';
                            (acc[key] = acc[key] || { name: n.project_name || 'Unknown project', id: n.project_id, items: [] }).items.push(n);
                            return acc;
                          }, {})
                        ).map(([pid, group]) => (
                          <div key={pid} style={{ marginBottom: 18 }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              marginBottom: 8, paddingBottom: 6,
                              borderBottom: '2px solid #ecf0f1',
                            }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#2c3e50' }}>
                                📁 {group.id
                                  ? <Link to={`/projects/${group.id}`}>{group.name}</Link>
                                  : group.name}
                              </span>
                              <span style={{ fontSize: 12, color: '#7f8c8d' }}>
                                {group.items.length} note{group.items.length === 1 ? '' : 's'}
                              </span>
                            </div>

                            {group.items.map(n => {
                              const tagged = n.tagged_tasks || [];
                              const myTaskIds = r.tasks.map(t => t.task_id);
                              const relevant = tagged.some(tg => myTaskIds.includes(tg.task_id));
                              return (
                                <div
                                  key={n.note_id}
                                  style={{
                                    padding: '12px 15px',
                                    borderLeft: `4px solid ${relevant ? '#3498db' : '#e1e8ed'}`,
                                    background: relevant ? '#f0f7ff' : '#f8f9fa',
                                    borderRadius: 8,
                                    marginBottom: 10,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                      {n.note_type && <span className="badge badge-info">{n.note_type}</span>}
                                    </div>
                                    <span style={{ fontSize: 12, color: '#7f8c8d' }}>
                                      {n.author || 'Unknown'} · {n.created_at}
                                    </span>
                                  </div>

                                  <div style={{ whiteSpace: 'pre-wrap', color: '#495057' }}>{n.content}</div>

                                  {/* Which tasks this note is tagged to */}
                                  {tagged.length > 0 && (
                                    <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: 12, color: '#7f8c8d' }}>Tagged to:</span>
                                      {tagged.map(tg => {
                                        const mine = myTaskIds.includes(tg.task_id);
                                        return (
                                          <span
                                            key={tg.task_id}
                                            title={mine ? 'This resource works on this task this week' : 'Tagged task not in this week\u2019s allocation'}
                                            style={{
                                              fontSize: 12,
                                              fontWeight: 600,
                                              padding: '3px 10px',
                                              borderRadius: 12,
                                              background: mine ? '#cfe2ff' : '#eef1f4',
                                              color:      mine ? '#084298' : '#7f8c8d',
                                              border: `1px solid ${mine ? '#b6d4fe' : '#e1e8ed'}`,
                                            }}
                                          >
                                            {mine ? '★ ' : ''}{tg.task_name}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WeeklyView;
